// /auth/callback — where Cognito returns after Google/Microsoft sign-in.
// Swaps the one-time code for tokens, then continues to the page the user wanted.
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { exchangeSsoCode } from "../auth/sso";
import { track } from "../track";

export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  // StrictMode runs effects twice in dev; a code can only be exchanged once
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Provider or Cognito error (e.g. the user cancelled at Google)
    const providerError = params.get("error_description") ?? params.get("error");
    const code = params.get("code");
    const state = params.get("state");
    const fail = (message: string) => {
      navigate(`/login?sso_error=${encodeURIComponent(message)}`, { replace: true });
    };

    if (providerError) {
      fail(providerError);
      return;
    }
    if (!code || !state) {
      fail("Sign-in was interrupted. Please try again.");
      return;
    }

    exchangeSsoCode(code, state)
      .then(({ tokens, next }) => {
        setSession(tokens);
        track("login", { method: "google" });
        navigate(next, { replace: true });
      })
      .catch((err: Error) => fail(err.message));
  }, [params, navigate, setSession]);

  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", color: "var(--brand-text-secondary)" }}>
      Signing you in…
    </div>
  );
}
